
export class RouterAddress {
  static ROUTE_PARAM_BUSINESS_HOME_ID = ":businessHomeRangeId";

  private static appendQueryParams(path: string, params: Record<string, string | undefined>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      const normalizedValue = String(value || "").trim();
      if (normalizedValue) {
        searchParams.set(key, normalizedValue);
      }
    });
    const query = searchParams.toString();
    return query ? `${path}?${query}` : path;
  }

  public static APP_ROOT = "/";
  public static PROFILE = "/profile";
  public static FAX_AUDIT_HISTORY = "/account/efax";
  public static WORKSPACES = "/workspaces";
  public static FACILITY_ADMIN_DASHBOARD = "/dashboard/facility-admin";
  public static FACILITY_WORKER_HOME = "/dashboard/facility-worker";
  public static ADMIN_HOME = "/admin/home";
  public static SUPPORT = "/support";
  public static BILLING = "/billing";
  public static SHARED_CALENDAR = "/calendar";
  public static SHARED_CONTENT_PAGE = "/shared-content";
  public static CONNECTIONS_PAGE = "/connections";

  public static LOGIN_PAGE = "/login";
  public static SIGN_UP = "/signup";
  public static CONFIRM_REGISTRATION = "/confirmRegistration";
  public static OTP_VERIFICATION = "/otpVerification";
  public static FORGOT_PASSWORD = "/forgotPassword";
  public static RESET_PASSWORD = "/resetPassword";
  public static NOT_SIGNED_IN = "/public";
  public static PUBLIC_INTAKE = "/resident/screening/:token";
  public static LEGACY_PUBLIC_INTAKE = "/intake/:token";

  public static PROVIDER_HOME = "/provider/home";
  public static HOME_INTAKE_RECEIVED_PAGE = "/provider/home/:homeId/resident/screening/";
  public static PROVIDER_NURSE_HOME = "/provider/nurse/home";
  public static AFH_DETAILS_PAGE = "/afh/:homeId";

  public static EMPLOYEE_HOME = "/employee/home";
  public static CAREGIVER_HOME = "/caregiver/home";
  public static CAREGIVER_ATTENDANCE_PAGE = "/caregiver/time-and-pay";
  public static CAREGIVER_JOB_BINDER_PAGE = "/caregiver/binder";
  public static FACILITY_WORKER_ATTENDANCE_PAGE = "/facility-worker/time-and-pay";
  public static FACILITY_WORKER_JOB_BINDER_PAGE = "/facility-worker/binder";
  public static NURSE_JOB_BINDER_PAGE = "/nurse/binder";
  public static MHP_JOB_BINDER_PAGE = "/mhp/binder";
  public static SHARED_BINDER_PAGE = "/binder/share/:shareId/";
  public static FAMILY_MEMBER_HOME = "/familyMember/home";
  public static VENDOR_HOME = "/vendor/home";
  public static NURSE_HOME = "/nurse/home";
  public static MHP_HOME = "/mhp/home";
  public static PROVIDER_NURSE_RESIDENT_ASSESSMENT_PAGE = "/provider/nurse/resident/:residentId/assessment/";
  public static PROVIDER_NURSE_RESIDENT_PROFILE_PAGE = "/provider/nurse/resident/:residentId/profile/";
  public static PROVIDER_NURSE_RESIDENT_NCP_PAGE = "/provider/nurse/resident/:residentId/ncp/";
  public static PROVIDER_NURSE_RESIDENT_DELEGATION_PAGE = "/provider/nurse/resident/:residentId/delegation/";
  public static PROVIDER_NURSE_RESIDENT_PODIATRIST_PAGE = "/provider/nurse/resident/:residentId/FootNailCare/";
  public static PROVIDER_NURSE_RESIDENT_BINDER_PAGE = "/provider/nurse/resident/:residentId/binder/";
  public static PROVIDER_NURSE_DELEGATION_FORM_PAGE = "/provider/nurse/resident/:residentId/delegation/form/:formCode/";
  public static NURSE_RESIDENT_ASSESSMENT_PAGE = "/nurse/resident/:residentId/assessment/";
  public static NURSE_RESIDENT_PROFILE_PAGE = "/nurse/resident/:residentId/profile/";
  public static NURSE_RESIDENT_NCP_PAGE = "/nurse/resident/:residentId/ncp/";
  public static NURSE_RESIDENT_BINDER_PAGE = "/nurse/resident/:residentId/binder/";
  public static NURSE_ASSESSMENT_FORM_PAGE = "/nurse/resident/:residentId/assessment-form/";
  public static NURSE_RESIDENT_DELEGATION_PAGE = "/nurse/resident/:residentId/delegation/";
  public static NURSE_RESIDENT_PODIATRIST_PAGE = "/nurse/resident/:residentId/FootNailCare/";
  public static NURSE_DELEGATION_REFERRAL_FORM_PAGE = "/nurse/resident/:residentId/delegation/referral/";
  public static NURSE_DELEGATION_FORM_PAGE = "/nurse/resident/:residentId/delegation/form/:formCode/";
  public static PROVIDER_RESIDENT_PROFILE_PAGE = "/home/:homeId/resident/:residentId/profile/";
  public static PROVIDER_RESIDENT_ASSESSMENT_PAGE = "/home/:homeId/resident/:residentId/assessment/";
  public static PROVIDER_RESIDENT_ASSESSMENT_EDITOR_PAGE = "/home/:homeId/resident/:residentId/assessment/editor/";
  public static PROVIDER_RESIDENT_DELEGATION_PAGE = "/home/:homeId/resident/:residentId/delegation/";
  public static PROVIDER_RESIDENT_DELEGATION_FORM_PAGE = "/home/:homeId/resident/:residentId/delegation/form/:formCode/";

  public static AI_COMPANION = "/search_companion";

  public static HOME_BINDER_PAGE = "/home/:homeId/binder/";
  public static RESIDENT_BINDER_PAGE = "/home/:homeId/resident/:residentId/binder/";
  public static HOME_DOCUMENTS_PAGE = "/home/:homeId/documents/";
  public static RESIDENT_DOCUMENTS_PAGE = "/home/:homeId/resident/:residentId/documents/";
  public static CAREGIVER_DOCUMENTS_PAGE = "/home/:homeId/caregiver/:caregiverId/documents/";
  public static USER_DOCUMENTS_PAGE = "/user/:userId/documents";

  public static USER_REVIEWS_PAGE = "/reviews";

  public static RESIDENT_DETAILS_PAGE = "/home/:homeId/resident/:residentId/details/";
  public static RESIDENT_NCP_PAGE = "/home/:homeId/resident/:residentId/ncp/";
  public static RESIDENT_ESF_CARE_PLAN_PAGE = "/home/:homeId/resident/:residentId/esf/careplan/";
  public static CAREGIVER_DETAILS_PAGE = "/home/:homeId/caregiver/:caregiverId/details/";
  public static HOME_STAFF_MANAGEMENT_PAGE = "/home/:homeId/staff/";
  public static HOME_CARE_ACTIVITY_PAGE = "/home/:homeId/care-activity/";
  public static SHARED_HOME_CARE_ACTIVITY_PAGE = "/shared/home/:homeId/care-activity/";
  public static HOME_FACILITY_TASKS_PAGE = "/home/:homeId/facility-tasks/";
  public static CAREGIVER_HOME_FACILITY_TASKS_PAGE = "/caregiver/home/:homeId/facility-tasks/";
  public static FACILITY_WORKER_HOME_FACILITY_TASKS_PAGE = "/facility-worker/home/:homeId/facility-tasks/";
  public static SHARED_HOME_FACILITY_TASKS_PAGE = "/shared/home/:homeId/facility-tasks/";
  public static RESIDENT_SCHEDULES_PAGE = "/home/:homeId/resident/:residentId/schedules/";
  public static PROVIDER_RESIDENT_CARE_PLAN_PAGE = "/provider/resident/:residentId/carePlan/";
  public static PROVIDER_RESIDENT_EMAR_PAGE = "/provider/resident/:residentId/emar/";
  public static PROVIDER_RESIDENT_ADL_PAGE = "/provider/resident/:residentId/adl/";
  public static PROVIDER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE = "/provider/resident/:residentId/dailyBehaviorLog/";
  public static PROVIDER_RESIDENT_INCIDENT_REPORT_PAGE = "/provider/resident/:residentId/incidentReport/";
  public static PROVIDER_RESIDENT_DAILY_NOTES_PAGE = "/provider/resident/:residentId/dailyNotes/";
  public static PROVIDER_RESIDENT_ENTRY_TRACKER_PAGE = "/provider/resident/:residentId/entryTracker/";
  public static CAREGIVER_RESIDENT_CARE_PLAN_PAGE = "/caregiver/resident/:residentId/carePlan/";
  public static CAREGIVER_RESIDENT_EMAR_PAGE = "/caregiver/resident/:residentId/emar/";
  public static CAREGIVER_RESIDENT_ADL_PAGE = "/caregiver/resident/:residentId/adl/";
  public static CAREGIVER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE = "/caregiver/resident/:residentId/dailyBehaviorLog/";
  public static CAREGIVER_RESIDENT_INCIDENT_REPORT_PAGE = "/caregiver/resident/:residentId/incidentReport/";
  public static CAREGIVER_RESIDENT_DAILY_NOTES_PAGE = "/caregiver/resident/:residentId/dailyNotes/";
  public static SHARED_RESIDENT_ASSESSMENT_PAGE = "/shared/home/:homeId/resident/:residentId/assessment/";
  public static SHARED_RESIDENT_PROFILE_PAGE = "/shared/home/:homeId/resident/:residentId/profile/";
  public static SHARED_RESIDENT_NCP_PAGE = "/shared/home/:homeId/resident/:residentId/careplan/";
  public static SHARED_RESIDENT_ESF_CARE_PLAN_PAGE = "/shared/home/:homeId/resident/:residentId/esf/careplan/";
  public static SHARED_RESIDENT_CARE_PLAN_PAGE = "/shared/home/:homeId/resident/:residentId/search-care-plan/";
  public static SHARED_RESIDENT_EMAR_PAGE = "/shared/home/:homeId/resident/:residentId/emar/";
  public static SHARED_RESIDENT_ADL_PAGE = "/shared/home/:homeId/resident/:residentId/adl/";
  public static SHARED_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE = "/shared/home/:homeId/resident/:residentId/dailyBehaviorLog/";
  public static SHARED_RESIDENT_INCIDENT_REPORT_PAGE = "/shared/home/:homeId/resident/:residentId/incidentReport/";
  public static SHARED_RESIDENT_DAILY_NOTES_PAGE = "/shared/home/:homeId/resident/:residentId/dailyNotes/";
  public static SHARED_RESIDENT_ENTRY_TRACKER_PAGE = "/shared/home/:homeId/resident/:residentId/entryTracker/";
  public static SHARED_RESIDENT_DELEGATION_PAGE = "/shared/home/:homeId/resident/:residentId/delegation/";
  public static SHARED_RESIDENT_DELEGATION_FORM_PAGE = "/shared/home/:homeId/resident/:residentId/delegation/form/:formCode/";
  public static SHARED_RESIDENT_PODIATRIST_PAGE = "/shared/home/:homeId/resident/:residentId/FootNailCare/";
  public static SHARED_RESIDENT_BINDER_PAGE = "/shared/home/:homeId/resident/:residentId/binder/";

  public static USER_REMINDER_PAGE = "/reminder/";

  public static FIND_CAREGIVERS = "/caregiver/search";
  public static SEARCH_JOB = "/job/search";
  public static LISTINGS = "/listings";
  public static PROVIDER_DIRECTORY_MAP = "/provider-directory-map";

  public static MANAGE_WEBSITE = "/manageWebsite";
  public static ADMIN_PAGE = "/admin";
  public static PUBLIC_WEBSITE = "/site/:state/:businessName";
  public static PUBLIC_WEBSITE_SUBPAGE = "/site/:state/:businessName/:pageSlug";
  public static PUBLISH_WEB = "/home/:state/:businessName";
  public static PUBLISH_WEB_SUBPAGE = "/home/:state/:businessName/:pageSlug";
  public static PRIVACY_PAGE = "/privacyPolicy";
  public static TERMS_OF_SERVICE_PAGE = "/termsOfService";
  public static BUSINESS_ASSOCIATE_AGREEMENT_PAGE = "/businessAssociateAgreement";
  public static HIPAA_COMPLIANCE_PAGE = "/hipaaCompliance";
  public static ABOUT_US_PAGE = "/aboutUs";
  public static WHY_USE_HUB_PAGE = "/why-use-hub";
  public static LEGACY_WHY_AFH_HUB_PAGE = "/why-afh-hub";
  public static EMAR_PAGE = "/emar";
  public static ADL_PAGE = "/adl";
  public static PRICING_PAGE = "/pricing";
  public static BOOK_DEMO_PAGE = "/book-demo";
  public static LIVE_DEMO_PAGE = "/live-preview";
  public static LEGACY_LIVE_DEMO_PAGE = "/live-demo";
  public static PLATFORM_FEATURES_PAGE = "/platformFeatures";
  public static TUTORIALS_PAGE = "/tutorials";
  public static RELEASE_NOTES_PAGE = "/release-notes";
  public static EMAIL_PREFERENCES_PAGE = "/emailPreferences";

  public static getResidentDetailsPagePath(homeId: string, residentId: string, tab?: string, tool?: string): string {
    const basePath = RouterAddress.RESIDENT_DETAILS_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
    const params = new URLSearchParams();
    if (String(tab || "").trim()) {
      params.set("tab", String(tab).trim());
    }
    if (String(tool || "").trim()) {
      params.set("tool", String(tool).trim());
    }
    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
  }

  public static getResidentNCPPagePath(homeId: string, residentId: string): string {
    return RouterAddress.RESIDENT_NCP_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getProviderResidentEntryTrackerPagePath(residentId: string): string {
    return RouterAddress.PROVIDER_RESIDENT_ENTRY_TRACKER_PAGE.replace(":residentId", residentId);
  }

  public static getSharedResidentEntryTrackerPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_ENTRY_TRACKER_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getResidentEsfCarePlanPagePath(homeId: string, residentId: string): string {
    return RouterAddress.RESIDENT_ESF_CARE_PLAN_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getResidentCarePlanPagePathForFacility(homeId: string, residentId: string, facilityType?: string): string {
    return String(facilityType || "").trim().toLowerCase() === "esf"
      ? RouterAddress.getResidentEsfCarePlanPagePath(homeId, residentId)
      : RouterAddress.getResidentNCPPagePath(homeId, residentId);
  }

  public static getNurseResidentAssessmentPagePath(residentId: string): string {
    return RouterAddress.NURSE_RESIDENT_ASSESSMENT_PAGE.replace(":residentId", residentId);
  }

  public static getProviderNurseResidentAssessmentPagePath(residentId: string): string {
    return RouterAddress.PROVIDER_NURSE_RESIDENT_ASSESSMENT_PAGE.replace(":residentId", residentId);
  }

  public static getNurseAssessmentFormPagePath(residentId: string): string {
    return RouterAddress.getNurseResidentAssessmentPagePath(residentId);
  }

  public static getNurseResidentProfilePagePath(residentId: string): string {
    return RouterAddress.NURSE_RESIDENT_PROFILE_PAGE.replace(":residentId", residentId);
  }

  public static getProviderNurseResidentProfilePagePath(residentId: string): string {
    return RouterAddress.PROVIDER_NURSE_RESIDENT_PROFILE_PAGE.replace(":residentId", residentId);
  }

  public static getNurseResidentNcpPagePath(residentId: string): string {
    return RouterAddress.NURSE_RESIDENT_NCP_PAGE.replace(":residentId", residentId);
  }

  public static getProviderNurseResidentNcpPagePath(residentId: string): string {
    return RouterAddress.PROVIDER_NURSE_RESIDENT_NCP_PAGE.replace(":residentId", residentId);
  }

  public static getProviderNurseResidentPodiatristPagePath(residentId: string): string {
    return RouterAddress.PROVIDER_NURSE_RESIDENT_PODIATRIST_PAGE.replace(":residentId", residentId);
  }

  public static getNurseResidentBinderPagePath(residentId: string): string {
    return RouterAddress.NURSE_RESIDENT_BINDER_PAGE.replace(":residentId", residentId);
  }

  public static getProviderNurseResidentBinderPagePath(residentId: string): string {
    return RouterAddress.PROVIDER_NURSE_RESIDENT_BINDER_PAGE.replace(":residentId", residentId);
  }

  public static getProviderResidentProfilePagePath(homeId: string, residentId: string): string {
    return RouterAddress.PROVIDER_RESIDENT_PROFILE_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getNurseResidentDelegationPagePath(residentId: string): string {
    return RouterAddress.NURSE_RESIDENT_DELEGATION_PAGE.replace(":residentId", residentId);
  }

  public static getNurseResidentPodiatristPagePath(residentId: string): string {
    return RouterAddress.NURSE_RESIDENT_PODIATRIST_PAGE.replace(":residentId", residentId);
  }

  public static getProviderNurseResidentDelegationPagePath(residentId: string): string {
    return RouterAddress.PROVIDER_NURSE_RESIDENT_DELEGATION_PAGE.replace(":residentId", residentId);
  }

  public static getProviderResidentAssessmentPagePath(homeId: string, residentId: string): string {
    return RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getProviderResidentAssessmentEditorPagePath(homeId: string, residentId: string): string {
    return RouterAddress.PROVIDER_RESIDENT_ASSESSMENT_EDITOR_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getProviderResidentDelegationPagePath(homeId: string, residentId: string): string {
    return RouterAddress.PROVIDER_RESIDENT_DELEGATION_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentProfilePagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_PROFILE_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentAssessmentPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_ASSESSMENT_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentNcpPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_NCP_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentEsfCarePlanPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_ESF_CARE_PLAN_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentCarePlanPagePathForFacility(homeId: string, residentId: string, facilityType?: string): string {
    return String(facilityType || "").trim().toLowerCase() === "esf"
      ? RouterAddress.getSharedResidentEsfCarePlanPagePath(homeId, residentId)
      : RouterAddress.getSharedResidentNcpPagePath(homeId, residentId);
  }

  public static getSharedResidentCarePlanPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_CARE_PLAN_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentPodiatristPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_PODIATRIST_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentEmarPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_EMAR_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentAdlPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_ADL_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentDailyBehaviorLogPagePath(homeId: string, residentId: string, section?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.SHARED_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE.replace(":homeId", homeId).replace(":residentId", residentId),
      { section },
    );
  }

  public static getHomeCareActivityPagePath(homeId: string): string {
    return RouterAddress.HOME_CARE_ACTIVITY_PAGE.replace(":homeId", homeId);
  }

  public static getHomeIntakeReceivedPagePath(homeId: string): string {
    return RouterAddress.HOME_INTAKE_RECEIVED_PAGE.replace(":homeId", homeId);
  }

  public static getSharedHomeCareActivityPagePath(homeId: string): string {
    return RouterAddress.SHARED_HOME_CARE_ACTIVITY_PAGE.replace(":homeId", homeId);
  }

  public static getHomeFacilityTasksPagePath(homeId: string): string {
    return RouterAddress.HOME_FACILITY_TASKS_PAGE.replace(":homeId", homeId);
  }

  public static getCaregiverHomeFacilityTasksPagePath(homeId: string): string {
    return RouterAddress.getHomeFacilityTasksPagePath(homeId);
  }

  public static getFacilityWorkerHomeFacilityTasksPagePath(homeId: string): string {
    return RouterAddress.getHomeFacilityTasksPagePath(homeId);
  }

  public static getSharedHomeFacilityTasksPagePath(homeId: string): string {
    return RouterAddress.SHARED_HOME_FACILITY_TASKS_PAGE.replace(":homeId", homeId);
  }

  public static getResidentSchedulesPagePath(homeId: string, residentId: string): string {
    return RouterAddress.RESIDENT_SCHEDULES_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentIncidentReportPagePath(homeId: string, residentId: string, section?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.SHARED_RESIDENT_INCIDENT_REPORT_PAGE.replace(":homeId", homeId).replace(":residentId", residentId),
      { section },
    );
  }

  public static getSharedResidentDailyNotesPagePath(homeId: string, residentId: string, section?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.SHARED_RESIDENT_DAILY_NOTES_PAGE.replace(":homeId", homeId).replace(":residentId", residentId),
      { section },
    );
  }

  public static getSharedResidentDelegationPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_DELEGATION_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedResidentBinderPagePath(homeId: string, residentId: string): string {
    return RouterAddress.SHARED_RESIDENT_BINDER_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getSharedDelegationFormPagePath(homeId: string, residentId: string, formCode: string, formId?: string): string {
    const basePath = RouterAddress.SHARED_RESIDENT_DELEGATION_FORM_PAGE
      .replace(":homeId", homeId)
      .replace(":residentId", residentId)
      .replace(":formCode", encodeURIComponent(formCode));
    return formId ? `${basePath}?formId=${encodeURIComponent(formId)}` : basePath;
  }

  public static getNurseResidentDelegationReferralPagePath(residentId: string): string {
    return RouterAddress.NURSE_DELEGATION_REFERRAL_FORM_PAGE.replace(":residentId", residentId);
  }

  public static getNurseDelegationFormPagePath(residentId: string, formCode: string, formId?: string): string {
    const basePath = RouterAddress.NURSE_DELEGATION_FORM_PAGE
      .replace(":residentId", residentId)
      .replace(":formCode", encodeURIComponent(formCode));
    return formId ? `${basePath}?formId=${encodeURIComponent(formId)}` : basePath;
  }

  public static getProviderNurseDelegationFormPagePath(residentId: string, formCode: string, formId?: string): string {
    const basePath = RouterAddress.PROVIDER_NURSE_DELEGATION_FORM_PAGE
      .replace(":residentId", residentId)
      .replace(":formCode", encodeURIComponent(formCode));
    return formId ? `${basePath}?formId=${encodeURIComponent(formId)}` : basePath;
  }

  public static getProviderDelegationFormPagePath(homeId: string, residentId: string, formCode: string, formId?: string): string {
    const basePath = RouterAddress.PROVIDER_RESIDENT_DELEGATION_FORM_PAGE
      .replace(":homeId", homeId)
      .replace(":residentId", residentId)
      .replace(":formCode", encodeURIComponent(formCode));
    return formId ? `${basePath}?formId=${encodeURIComponent(formId)}` : basePath;
  }

  public static getCaregiverDetailsPagePath(homeId: string, caregiverId: string): string {
    return RouterAddress.CAREGIVER_DETAILS_PAGE.replace(":homeId", homeId).replace(":caregiverId", caregiverId);
  }

  public static getHomeStaffManagementPagePath(homeId: string): string {
    return RouterAddress.HOME_STAFF_MANAGEMENT_PAGE.replace(":homeId", homeId);
  }

  public static getCaregiverResidentCarePlanPagePath(residentId: string): string {
    return RouterAddress.CAREGIVER_RESIDENT_CARE_PLAN_PAGE.replace(":residentId", residentId);
  }

  public static getCaregiverResidentEmarPagePath(residentId: string): string {
    return RouterAddress.CAREGIVER_RESIDENT_EMAR_PAGE.replace(":residentId", residentId);
  }

  public static getCaregiverResidentAdlPagePath(residentId: string): string {
    return RouterAddress.CAREGIVER_RESIDENT_ADL_PAGE.replace(":residentId", residentId);
  }

  public static getCaregiverResidentDailyBehaviorLogPagePath(residentId: string, section?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.CAREGIVER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE.replace(":residentId", residentId),
      { section },
    );
  }

  public static getCaregiverResidentIncidentReportPagePath(residentId: string, section?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.CAREGIVER_RESIDENT_INCIDENT_REPORT_PAGE.replace(":residentId", residentId),
      { section },
    );
  }

  public static getCaregiverResidentDailyNotesPagePath(residentId: string, section?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.CAREGIVER_RESIDENT_DAILY_NOTES_PAGE.replace(":residentId", residentId),
      { section },
    );
  }

  public static getCaregiverAttendancePagePath(range?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.CAREGIVER_ATTENDANCE_PAGE,
      { range },
    );
  }

  public static getFacilityWorkerAttendancePagePath(range?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.FACILITY_WORKER_ATTENDANCE_PAGE,
      { range },
    );
  }

  public static getProviderResidentCarePlanPagePath(residentId: string, homeId?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.PROVIDER_RESIDENT_CARE_PLAN_PAGE.replace(":residentId", residentId),
      { homeId },
    );
  }

  public static getProviderResidentEmarPagePath(residentId: string, homeId?: string): string {
    const basePath = RouterAddress.PROVIDER_RESIDENT_EMAR_PAGE.replace(":residentId", residentId);
    return homeId ? `${basePath}?homeId=${encodeURIComponent(homeId)}` : basePath;
  }

  public static getProviderResidentAdlPagePath(residentId: string, homeId?: string): string {
    const basePath = RouterAddress.PROVIDER_RESIDENT_ADL_PAGE.replace(":residentId", residentId);
    return homeId ? `${basePath}?homeId=${encodeURIComponent(homeId)}` : basePath;
  }

  public static getProviderResidentDailyBehaviorLogPagePath(residentId: string, section?: string, homeId?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.PROVIDER_RESIDENT_DAILY_BEHAVIOR_LOG_PAGE.replace(":residentId", residentId),
      { section, homeId },
    );
  }

  public static getProviderResidentIncidentReportPagePath(residentId: string, section?: string, homeId?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.PROVIDER_RESIDENT_INCIDENT_REPORT_PAGE.replace(":residentId", residentId),
      { section, homeId },
    );
  }

  public static getProviderResidentDailyNotesPagePath(residentId: string, section?: string, homeId?: string): string {
    return RouterAddress.appendQueryParams(
      RouterAddress.PROVIDER_RESIDENT_DAILY_NOTES_PAGE.replace(":residentId", residentId),
      { section, homeId },
    );
  }

  public static getManageWebsitePath(homeId: string): string {
    return `${RouterAddress.MANAGE_WEBSITE}?homeId=${homeId}`;
  }

  public static getManageWebsitePreviewPath(homeId: string, templateKey: string): string {
    return `${RouterAddress.MANAGE_WEBSITE}?homeId=${homeId}&preview=true&templateKey=${templateKey}`;
  }

  public static getAFHDetailsPagePath(homeId: string | undefined): string {
    return RouterAddress.AFH_DETAILS_PAGE.replace(":homeId", homeId!);
  }

  public static getHomeBinderPagePath(homeId: string | undefined): string {
    return RouterAddress.HOME_BINDER_PAGE.replace(":homeId", homeId!);
  }

  public static getCaregiverJobBinderPagePath(): string {
    return RouterAddress.CAREGIVER_JOB_BINDER_PAGE;
  }

  public static getFacilityWorkerJobBinderPagePath(): string {
    return RouterAddress.FACILITY_WORKER_JOB_BINDER_PAGE;
  }

  public static getNurseJobBinderPagePath(): string {
    return RouterAddress.NURSE_JOB_BINDER_PAGE;
  }

  public static getMhpJobBinderPagePath(): string {
    return RouterAddress.MHP_JOB_BINDER_PAGE;
  }

  public static getSharedBinderPagePath(shareId: string): string {
    return RouterAddress.SHARED_BINDER_PAGE.replace(":shareId", shareId);
  }

  public static getHomeDocumentsPagePath(homeId: string | undefined): string {
    return RouterAddress.HOME_DOCUMENTS_PAGE.replace(":homeId", homeId!);
  }

  public static getResidentBinderPagePath(homeId: string, residentId: string): string {
    return RouterAddress.RESIDENT_BINDER_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getResidentDocumentsPagePath(homeId: string, residentId: string): string {
    return RouterAddress.RESIDENT_DOCUMENTS_PAGE.replace(":homeId", homeId).replace(":residentId", residentId);
  }

  public static getCaregiverDocumentsPagePath(homeId: string, caregiverId: string): string {
    return RouterAddress.CAREGIVER_DOCUMENTS_PAGE.replace(":homeId", homeId).replace(":caregiverId", caregiverId);
  }

  public static getUserDocumentsPagePath(userId: string | undefined): string {
    return RouterAddress.USER_DOCUMENTS_PAGE.replace(":userId", userId!);
  }
}
